<template>
    <!-- This is currently used by edumate news and directly inside the schoolbox learning moments widget -->
    <div
        class="mt-6 mx-4"
        data-cy-comments
    >
        <div
            v-if="config.data.send_comment_types.length"
            class="flex w-full space-x-4 mt-3 mb-4"
        >
            <UIAvatar
                v-if="config.data.send_comment_types.length === 1 && config.data.send_comment_types[0].avatar"
                :avatar="config.data.send_comment_types[0].avatar"
                class="shrink-0"
                size-override="small"
            />
            <div
                :id="addCommentModalUuid"
                class="grow border rounded-button-md py-2 px-3 text-sm text-gray-500 border-gray-300 cursor-pointer"
            >
                Add a comment...
            </div>
        </div>
        <div
            v-for="(item, index) in items"
            :key="index"
            class="my-3 flex w-full space-x-4"
        >
            <UIAvatar
                :avatar="item.avatar"
                class="shrink-0"
                size-override="small"
            />
            <div class="grow">
                <div class="surface-neutral p-2 rounded-lg">
                    <UIHtml
                        :html="item.avatar.description"
                        class="prose prose-sm text-sm leading-5 font-medium max-w-full mt-1 mb-1"
                        style="overflow-wrap: anywhere;"
                    />
                    <!-- Remove the prose max-width if there's a table in the content -->
                    <UIHtml
                        :html="item.body"
                        class="w-full prose prose-sm has-[table,iframe]:max-w-none text-sm leading-4.5 max-w-full mb-1"
                        style="overflow-wrap: anywhere;"
                    />
                </div>
                <div class="flex item-center w-full">
                    <UIHtml
                        v-if="item.subtitle"
                        :html="item.subtitle"
                        class="prose prose-sm text-xs leading-4.5 max-w-full mt-2"
                        style="overflow-wrap: anywhere;"
                    />
                    <DButton
                        v-if="item.menu_items?.length"
                        icon="ellipsis-vertical"
                        theme="transparent"
                        size="sm"
                        class="ml-auto"
                        @click="openCommentMenuModal(item)"
                    />
                </div>
            </div>
        </div>
        <DButton
            v-if="nextPageUrl"
            theme="text-link"
            class="mt-4"
            :loading="nextPageLoading"
            @click="loadNextPage()"
        >
            View more comments
        </DButton>
    </div>
    <IonModal
        ref="addCommentModal"
        :trigger="addCommentModalUuid"
        :initial-breakpoint="0.5"
        :breakpoints="[0, 0.5]"
        :can-dismiss="canDismissCommentModal"
        class="ds-modal-bottom-sheet"
        @did-present="onCommentModal"
    >
        <!-- Ionic modal provides --max-height -->
        <div class="pt-6 h-(--max-height) flex flex-col">
            <IonContent
                ref="contentScroll"
                class="flex-1"
            >
                <template v-if="!dismissResolver">
                    <div class="flex items-center px-4 mb-4">
                        <DGroup v-if="config.data.send_comment_types.length > 1">
                            <DButton
                                v-for="(type, index) in config.data.send_comment_types"
                                :key="`tab-${index}`"
                                :theme="index === selectedTypeIndex ? 'outline' : 'base'"
                                :class="index === selectedTypeIndex ? 'z-10' : null"
                                @click="switchType(index)"
                            >
                                {{ type.title }}
                            </DButton>
                        </DGroup>
                        <DButton
                            class="ml-auto"
                            :theme="selectedType.button.type"
                            :left-icon="selectedType.button.icon ? `ph:${selectedType.button.icon}` : null"
                            :loading="submitting"
                            :disabled="!comment"
                            @click="submitComment(selectedType.button)"
                        >
                            {{ selectedType.button.title }}
                        </DButton>
                    </div>
                    <div
                        v-if="selectedType.avatar"
                        class="flex items-center px-4 mb-4"
                    >
                        <UIAvatar
                            class="mr-2"
                            :avatar="selectedType.avatar"
                            size-override="small"
                        />
                        <div>
                            <div class="text-sm font-medium">
                                {{ selectedType.avatar.description }}
                            </div>
                            <div class="text-xs text-gray-700">
                                {{ selectedType.avatar_subtext }}
                            </div>
                        </div>
                    </div>
                    <div class="py-2 px-3">
                        <DField
                            id="comment"
                            :form-errors="errors"
                        >
                            <DInput
                                ref="commentInput"
                                v-model="comment"
                                aria-label="Comment"
                                tag="textarea"
                                placeholder="Write a comment..."
                                :rows="4"
                            />
                        </DField>
                    </div>
                </template>
                <template v-else>
                    <div class="w-full flex flex-col items-center">
                        <div class="text-center">
                            <h1 class="content-lg-ux-medium mb-8">
                                Discard comment?
                            </h1>
                            <p class="content-sm-ux-regular">
                                Are you sure you want to leave?<br>Your comment won't be saved.
                            </p>
                        </div>
                        <div class="w-full max-w-xl flex space-x-3 mt-6 px-4">
                            <DButton
                                wide
                                theme="base"
                                @click="dismissResolver(false)"
                            >
                                Cancel
                            </DButton>
                            <DButton
                                wide
                                @click="dismissResolver(true)"
                            >
                                Yes, discard
                            </DButton>
                        </div>
                    </div>
                </template>
            </IonContent>
        </div>
    </IonModal>
    <IonModal
        ref="commentMenuModal"
        :initial-breakpoint="1"
        :breakpoints="[0, 1]"
        class="ds-modal-bottom-sheet"
    >
        <div class="mt-6 mb-2 pb-(--beyond-safe-area-inset-bottom)">
            <div v-if="selectedComment">
                <UISimple
                    v-for="(item, index) in selectedComment.menu_items ?? []"
                    :key="`menu-item-${index}`"
                    :title="item.title"
                    :icon="{ icon: item.icon, icon_url: null, color: null }"
                    @click="openMenuItem(item)"
                />
            </div>
        </div>
    </IonModal>
</template>

<script setup>
    import { useRouter } from 'vue-router'
    import { v4 as uuidv4 } from 'uuid'
    import {
        DInput, DField, DButton, DGroup,
    } from '@digistorm/spark'
    import UIAvatar from '@/components/ui/UIAvatar.vue'
    import UIHtml from '@/components/ui/UIHtml.vue'
    import { api } from '@/services'

    const contextualEvents = inject('contextualEvents')

    const props = defineProps({
        config: Object,
    })

    const router = useRouter()
    const moduleHelpers = useModuleHelpers()
    const { snackbar } = useSnackbars()

    const items = ref(props.config.items)
    const addCommentModalUuid = ref(uuidv4())
    const selectedTypeIndex = ref(0)
    const comment = ref('')
    const addCommentModal = useTemplateRef('addCommentModal')
    const commentMenuModal = useTemplateRef('commentMenuModal')
    const selectedComment = ref(null)
    const errors = ref({})
    const submitting = ref(false)
    const modal = ref()
    const commentInput = ref()
    const dismissCommentModalAnswer = ref(null)
    const dismissResolver = ref(null)

    const nextPageUrl = ref(props.config.data.next)
    const nextPageLoading = ref(false)

    const selectedType = computed(() => {
        return props.config.data.send_comment_types[selectedTypeIndex.value]
    })

    const switchType = (index) => {
        selectedTypeIndex.value = index
    }

    const onCommentModal = () => {
        dismissCommentModalAnswer.value = null
        commentInput.value.$refs.input.focus()
    }

    const submitComment = (button) => {
        button.link.payload = { comment: comment.value }

        submitting.value = true

        moduleHelpers.handleClick(router, button, null, false, contextualEvents.value)
            .then((appModule) => {
                dismissCommentModalAnswer.value = true
                addCommentModal.value.$el.dismiss()
                comment.value = ''
                if (!appModule || !appModule.id) {
                    return
                }
                router.push({ name: 'view', params: { id: appModule.id } })
            })
            .catch((error) => {
                if (error?.response?.status === 422) {
                    errors.value = error.response.data?.errors
                    return
                }

                console.error('error', error)
                snackbar({
                    message: 'Error submitting form',
                    type: 'error',
                })
            })
            .finally(() => {
                submitting.value = false
            })
    }

    const openCommentMenuModal = (item) => {
        selectedComment.value = item
        commentMenuModal.value.$el?.present()
    }

    const canDismissCommentModal = async () => {
        if (dismissCommentModalAnswer.value !== null) {
            return dismissCommentModalAnswer.value
        }
        if (!comment.value) {
            return true
        }

        const promise = new Promise((resolve) => {
            dismissResolver.value = resolve
        })
            .then((dismiss) => {
                if (dismiss) {
                    comment.value = ''
                }
                return dismiss
            })
            .finally(() => {
                dismissResolver.value = null
            })

        dismissCommentModalAnswer.value = await promise

        // For some reason this callback is called multiple times, so reset after a short delay
        setTimeout(() => {
            dismissCommentModalAnswer.value = null
        }, 500)

        return dismissCommentModalAnswer.value
    }

    const openMenuItem = (item) => {
        moduleHelpers.handleClick(router, item, null, true, contextualEvents.value)
            .catch((e) => {
                console.log('Failed to open link', e)
            })
            .finally(() => {
                commentMenuModal.value.$el?.dismiss(null, 'cancel')
            })
    }

    const loadNextPage = () => {
        if (!nextPageUrl.value || nextPageLoading.value) {
            return
        }
        nextPageLoading.value = true
        api.get(nextPageUrl.value)
            .then(({ data }) => {
                items.value = items.value.concat(data.items)
                nextPageUrl.value = data.data.next
            })
            .catch((error) => {
                console.error('error', error)
                snackbar({
                    message: 'Error loading more comments',
                    type: 'error',
                })
            })
            .finally(() => {
                nextPageLoading.value = false
            })
    }

    watch(() => props.config.items, (newItems) => {
        items.value = newItems
    }, { immediate: true })

    watch(() => props.config.data.next, (newNext) => {
        nextPageUrl.value = newNext
    }, { immediate: true })
</script>
